import {
  ArrayInput,
  AutocompleteInput,
  BooleanField,
  BooleanInput,
  Button,
  Create,
  Datagrid,
  Edit,
  EditButton,
  List,
  maxLength,
  minLength,
  NumberInput,
  required,
  SimpleForm,
  SimpleFormIterator,
  TextField,
  TextInput,
  usePermissions,
  useRecordContext,
  useRedirect,
} from "react-admin";
import debounce from "lodash/debounce";
import PlayArrow from "@material-ui/icons/PlayArrow";
import { cloneElement } from "react";
import { Wordlist } from "../generated";
import { Word } from "../generated";

const validateName = [required(), minLength(2), maxLength(50)];
const hiddenStyle = { display: "none" };
const width = { "max-width": "500px" };

const WordlistTitle = ({ record }: { record: any }) => {
  return <span>Wordlist {record ? `"${record.name}"` : ""}</span>;
};

const WordlistListActionToolbar = ({
  children,
  ...props
}: {
  children: any;
}) => {
  return (
    <div>
      {children.map((button: any) => cloneElement(button, { ...props }))}
    </div>
  );
};

const uniqueWords = (words: Array<Word | undefined>) => {
  const wordSet = new Set();
  for (const word of words) {
    if (word === undefined || word.word === undefined) {
      continue;
    }
    if (wordSet.has(word.word)) {
      return `${word.word} appears more than once in the wordlist`;
    }
    wordSet.add(word.word);
  }
  return undefined;
};

const PracticeButton = (props: any) => {
  const record = useRecordContext(props);
  const redirect = useRedirect();
  return (
    <Button
      label="Practice"
      alignIcon="left"
      onClick={() => redirect(`/practice/${record.id}`)}
    >
      <PlayArrow />
    </Button>
  );
};

export const ConditionalEdit = (props: any) => {
  const { loaded: permissionsLoaded, permissions } = usePermissions();
  if (permissionsLoaded && permissions?.id === props.record.user_id) {
    return <EditButton {...props} />;
  }
  return <div />;
};

export const WordlistList = (props: any) => {
  return (
    <List {...props} filters={[]}>
      <Datagrid>
        <TextField source="name" />
        <BooleanField source="public" />
        <WordlistListActionToolbar>
          <PracticeButton key="practice" />
          <ConditionalEdit label="Edit" key="edit" />
        </WordlistListActionToolbar>
      </Datagrid>
    </List>
  );
};

export const WordlistEdit = (props: any) => (
  <Edit title={<WordlistTitle record={props} />} {...props}>
    <SimpleForm redirect="list">
      <TextInput source="name" validate={validateName} />
      <BooleanInput
        source="public"
        helperText="Make your wordlists public and share them with the community"
      />
      <NumberInput source="new_words_per_day" validate={required()} />
      <ArrayInput
        source="words"
        style={width}
        validate={[required(), debounce(uniqueWords, 500)]}
      >
        <SimpleFormIterator disableReordering={true}>
          <TextInput
            source="id"
            type="hidden"
            label={false}
            style={hiddenStyle}
          />
          <TextInput source="word" label="Word" />
        </SimpleFormIterator>
      </ArrayInput>
    </SimpleForm>
  </Edit>
);

export const WordlistCreate = (props: any) => (
  <Create {...props}>
    <SimpleForm redirect="list">
      <TextInput source="name" validate={validateName} />
      <BooleanInput
        source="public"
        helperText="Make your wordlists public and share them with the community"
      />
      <NumberInput
        source="new_words_per_day"
        validate={required()}
        defaultValue={10}
      />
      <AutocompleteInput
        helperText="English is the only option for now, more will be available soon"
        source="language"
        defaultValue={"en"}
        validate={required()}
        choices={[{ id: "en", name: "English" }]}
      />
      <ArrayInput source="words" validate={[required()]}>
        <SimpleFormIterator disableReordering={true}>
          <TextInput source="word" label="Word" />
        </SimpleFormIterator>
      </ArrayInput>
    </SimpleForm>
  </Create>
);
